import React from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';

const controlStyles = {
	container: (base) => ({ ...base, width: '100%' }),
	control: (base, state) => ({
		...base,
		minHeight: 38,
		backgroundColor: state.isDisabled ? 'var(--q-gray-50, #f8fafc)' : 'var(--q-surface-bg, #ffffff)',
		borderRadius: 'var(--q-radius-md, 8px)',
		borderColor: state.isFocused ? 'var(--q-brand-500, #187A8C)' : 'var(--q-surface-border, #e2e8f0)',
		boxShadow: state.isFocused ? 'var(--q-focus-ring, 0 0 0 3px rgba(24, 122, 140, .35))' : 'none',
		cursor: state.isDisabled ? 'not-allowed' : 'pointer',
		transition: 'border-color var(--q-transition-fast, 150ms ease), box-shadow var(--q-transition-fast, 150ms ease)',
		'&:hover': { borderColor: state.isDisabled ? 'var(--q-surface-border, #e2e8f0)' : 'var(--q-brand-500, #187A8C)' },
	}),
	valueContainer: (base) => ({ ...base, minHeight: 36, padding: '2px 10px' }),
	input: (base) => ({ ...base, color: 'var(--q-text-primary, #0f172a)', fontFamily: 'var(--q-font-family, inherit)', fontSize: 14 }),
	placeholder: (base) => ({ ...base, color: 'var(--q-text-muted, #64748b)', fontFamily: 'var(--q-font-family, inherit)', fontSize: 14 }),
	indicatorsContainer: (base) => ({ ...base, height: 36 }),
	clearIndicator: (base) => ({ ...base, color: 'var(--q-text-muted, #64748b)', '&:hover': { color: 'var(--q-danger-solid, #ef4444)' } }),
	dropdownIndicator: (base, state) => ({ ...base, color: state.isFocused ? 'var(--q-brand-500, #187A8C)' : 'var(--q-text-muted, #64748b)', '&:hover': { color: 'var(--q-brand-600, #166e7f)' } }),
	indicatorSeparator: (base) => ({ ...base, backgroundColor: 'var(--q-surface-border, #e2e8f0)' }),
	menuPortal: (base) => ({ ...base, zIndex: 3000 }),
	menu: (base) => ({ ...base, zIndex: 3000, border: '1px solid var(--q-surface-border, #e2e8f0)', borderRadius: 'var(--q-radius-md, 8px)', overflow: 'hidden', boxShadow: 'var(--q-surface-shadow, 0 4px 12px rgba(15, 23, 42, .12))', backgroundColor: 'var(--q-surface-bg, #ffffff)' }),
	menuList: (base) => ({ ...base, padding: 4 }),
	option: (base, state) => ({
		...base,
		padding: '9px 12px',
		borderRadius: 'var(--q-radius-sm, 6px)',
		fontSize: 14,
		color: state.isSelected ? 'var(--q-color-primary-contrast, #ffffff)' : 'var(--q-text-primary, #0f172a)',
		backgroundColor: state.isSelected ? 'var(--q-color-primary, #187A8C)' : state.isFocused ? 'var(--q-color-primary-subtle, #edfbfc)' : 'transparent',
		cursor: state.isDisabled ? 'not-allowed' : 'pointer',
		'&:active': { backgroundColor: 'var(--q-brand-100, #d1f4f8)' },
	}),
	multiValue: (base) => ({ ...base, borderRadius: 'var(--q-radius-sm, 6px)', backgroundColor: 'var(--q-color-primary-subtle, #edfbfc)' }),
	multiValueLabel: (base) => ({ ...base, color: 'var(--q-brand-700, #135a6a)', fontSize: 12 }),
	multiValueRemove: (base) => ({ ...base, color: 'var(--q-brand-600, #166e7f)', borderRadius: '0 6px 6px 0', '&:hover': { color: 'var(--q-danger-solid, #ef4444)', backgroundColor: 'var(--q-danger-bg, #fee2e2)' } }),
};
const spacing = { xs: 8, sm: 12, md: 16, lg: 24, xl: 32 };
const withSpacing = (value) => typeof value === 'string' ? `${spacing[value] ?? value}px` : typeof value === 'number' ? `${value}px` : value;

	const FormMultiSelect = ({
	options,
	data,
	value = [],
	onChange,
	disabled,
	placeholder,
	getOptionLabel,
	getOptionValue,
	className,
	clearable,
	searchable = true,
	label,
	withAsterisk,
	description,
	hint,
	error,
	mt,
	mb,
	style,
	wrapperClassName,
	id,
	...props
}) => {
	const generatedId = React.useId();
	const inputId = id || generatedId;
	const reactSelectApi = data === undefined && options !== undefined;
	const sourceOptions = data ?? options ?? [];
	const normalizedOptions = sourceOptions.map((option) => typeof option === 'string'
		? { value: option, label: option }
		: option.value !== undefined ? option : { value: option.id, label: option.name });
	const selected = (Array.isArray(value) ? value : []).map((item) => {
		if (typeof item === 'object' && item !== null) {
			return normalizedOptions.find((option) => getOptionValue(option) === getOptionValue(item)) ?? item;
		}
		return normalizedOptions.find((option) => String(getOptionValue(option)) === String(item)) ?? { value: item, label: String(item) };
	});
	return <div className={`q-form-group ${wrapperClassName || ''}`.trim()} style={{ marginTop: withSpacing(mt), marginBottom: withSpacing(mb), ...style }}>
		{label && <label className="q-form-label" htmlFor={inputId}>{label}{withAsterisk && <span className="q-form-required"> *</span>}</label>}
		<Select
			inputId={inputId}
			instanceId={inputId}
			isMulti
			closeMenuOnSelect={false}
			options={normalizedOptions}
			value={selected}
			onChange={(next) => onChange?.(reactSelectApi ? (next || []) : (next || []).map((option) => option.value))}
			isDisabled={disabled}
			placeholder={placeholder}
			getOptionLabel={getOptionLabel}
			getOptionValue={getOptionValue}
			isClearable={clearable}
			isSearchable={searchable}
			className={className}
			classNamePrefix="q-multi-select"
			styles={controlStyles}
			menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
			menuPosition="fixed"
			{...props}
		/>
		{error ? <div className="q-form-error">{error}</div> : hint || description ? <div className="q-form-hint">{hint ?? description}</div> : null}
	</div>
};

FormMultiSelect.propTypes = {
	options: PropTypes.array,
	data: PropTypes.array,
	value: PropTypes.array,
	onChange: PropTypes.func,
	disabled: PropTypes.bool,
	placeholder: PropTypes.node,
	getOptionLabel: PropTypes.func,
	getOptionValue: PropTypes.func,
	className: PropTypes.string,
	clearable: PropTypes.bool,
	searchable: PropTypes.bool,
	label: PropTypes.node,
	withAsterisk: PropTypes.bool,
	description: PropTypes.node,
	hint: PropTypes.node,
	error: PropTypes.node,
	mt: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	mb: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	style: PropTypes.object,
	wrapperClassName: PropTypes.string,
	id: PropTypes.string,
};

FormMultiSelect.defaultProps = {
	value: [],
	disabled: false,
	onChange: () => {},
	getOptionLabel: (option) => option.label ?? String(option.value ?? option),
	getOptionValue: (option) => String(option.value ?? option),
	clearable: false,
	searchable: true,
	withAsterisk: false,
};

export default FormMultiSelect;
